import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { downloadAssetImportTemplate, parseAssetImportFile, type ImportRowError, type ParsedAssetRow } from "@/lib/excel-import";
import { bulkCreateAssets, type AssetType, type ValueChainStage } from "@/lib/assets";
import { ApiError } from "@/lib/auth";

interface ExcelUploadDialogProps {
  assetTypes: AssetType[];
  stages: ValueChainStage[];
  onImported: () => void;
}

function assetTypeName(assetTypes: AssetType[], row: ParsedAssetRow): string {
  if (row.asset.customAssetTypeName) return row.asset.customAssetTypeName;
  return assetTypes.find((t) => t.id === row.asset.assetTypeId)?.name ?? "—";
}

export function ExcelUploadDialog({ assetTypes, stages, onImported }: ExcelUploadDialogProps) {
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<ImportRowError[] | null>(null);
  const [preview, setPreview] = useState<ParsedAssetRow[] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setErrors(null);
    setPreview(null);
    setSubmitting(false);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file after fixing it
    if (!file) return;

    setErrors(null);
    setPreview(null);

    const buffer = await file.arrayBuffer();
    const result = await parseAssetImportFile(file.name, buffer, assetTypes, stages);
    if (result.ok) {
      setPreview(result.rows);
    } else {
      setErrors(result.errors);
    }
  }

  async function handleConfirm() {
    if (!preview) return;
    setSubmitting(true);
    setErrors(null);
    try {
      await bulkCreateAssets(preview.map((r) => ({ row: r.row, asset: r.asset })));
      handleOpenChange(false);
      onImported();
    } catch (err) {
      if (err instanceof ApiError && Array.isArray((err.body as { errors?: unknown })?.errors)) {
        setErrors((err.body as { errors: ImportRowError[] }).errors);
      } else {
        setErrors([{ row: 0, message: err instanceof Error ? err.message : "Failed to import sites" }]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const hasOptions = assetTypes.length > 0 && stages.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!hasOptions}>
          Upload Excel
        </Button>
      </DialogTrigger>
      <DialogContent className={preview ? "max-w-3xl" : undefined}>
        <DialogHeader>
          <DialogTitle>Upload sites from Excel</DialogTitle>
          <DialogDescription>Download and fill the template with your data.</DialogDescription>
        </DialogHeader>

        {!preview && (
          <>
            <Button variant="outline" onClick={() => downloadAssetImportTemplate(assetTypes, stages)}>
              Download template
            </Button>
            <input type="file" accept=".xlsx" onChange={handleFileSelected} className="text-sm" />
          </>
        )}

        {errors && errors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <ul className="list-disc space-y-1 pl-4">
              {errors.map((err, i) => (
                <li key={i}>{err.row > 0 ? `Row ${err.row}: ${err.message}` : err.message}</li>
              ))}
            </ul>
          </div>
        )}

        {preview && (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site name</TableHead>
                  <TableHead>Asset type</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Importance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.map((r) => (
                  <TableRow key={r.row}>
                    <TableCell className="font-medium">{r.asset.name}</TableCell>
                    <TableCell>{assetTypeName(assetTypes, r)}</TableCell>
                    <TableCell>
                      {r.asset.city}, {r.asset.country}
                    </TableCell>
                    <TableCell>{r.asset.businessImportance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          {preview && (
            <Button onClick={handleConfirm} disabled={submitting}>
              {submitting ? "Importing..." : `Import ${preview.length} site${preview.length === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
